import './carousel.less';

import $ from 'jquery';
import React, { cloneElement, Children, Component } from 'react';

import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';

import debounce from 'lodash/debounce';

/*
 * This is an opinionated "accessible" carousel implementation.
 *
 * This component excludes any slide click handlers and leaves those details to
 * the implementation engineer. The only thing this component cares about is
 * what the current slide index is and with that it can do all the magic is
 * needs to.
 *
 * This implementation is meant to be accessible by default allowing you to
 * focus on the actual content of each slide rather than the required props to
 * fulfull compliance.
 *
 * Please notice:
 *
 *   - We are listening to the resize event. This allows us to resize the
 *     slides whenever widths change.
 *   - All calculations are based on the viewport element's width. This will
 *     fill 100% of the parent's width by its nature.
 *   - Because this doesn't handle the buttons and other elements typically
 *     found with a carousel you will need to make sure that those controls
 *     have the correct focus states and accessibility requirements.
 *   - It is best practice to have this component and its controls wrapped in a
 *     <div /> or <section /> element with `role="region"` defined on it. This
 *     helps designate to viewers that these elements are related in nature.
 */
class Carousel extends Component {

  constructor() {
    super();

    this.state = {
      viewportWidth: null
    };

    this.viewport = null;

    // we need something we can use to differentiate this carousel in the DOM
    this.resizeKey = 'carousel' + Math.floor(Math.random()*1000000);

    autobind(this);
  }

  componentDidMount() {
    $(window).on(
      `resize.${this.resizeKey}`,
      debounce(this.updateViewportWidth, 250)
    );
    
    // Set up intersection observer to detect when carousel becomes visible
    this.setupVisibilityObserver();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentSlideIdx !== this.props.currentSlideIdx && !this.state.viewportWidth) {
      this.updateViewportWidth();
    }
  }

  componentWillUnmount() {
    $(window).off(`resize.${this.resizeKey}`);
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
  }

  setupVisibilityObserver() {
    if (!window.IntersectionObserver) {
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.state.viewportWidth) {
          this.updateViewportWidth();
        }
      });
    }, {
      threshold: 0.1
    });

    if (this.viewport) {
      this.visibilityObserver.observe(this.viewport);
    }
  }

  updateViewportWidth() {
    if (!this.viewport) {
      return;
    }

    const newWidth = $(this.viewport).width();

    if (newWidth > 0) {
      this.setState({
        viewportWidth: newWidth
      });
    }
  }

  handleviewportRefReceived(ref) {
    this.viewport = ref;

    this.updateViewportWidth();
    
    if (this.visibilityObserver) {
      if (ref) {
        this.visibilityObserver.observe(ref);
      } else {
        this.visibilityObserver.disconnect();
      }
    }
  }

  render() {
    const {
      currentSlideIdx,
      children,
      viewportClassName,
      slidesClassName,
      slideClassName,
      viewportProps,
      slidesProps
    } = this.props;

    const {
      viewportWidth
    } = this.state;

    const totalSlides = children.length;

    const slidesStyle = viewportWidth ? {
      width: `${viewportWidth * totalSlides}px`,
      transform: `translate3d(-${viewportWidth * currentSlideIdx}px, 0px, 0px)`
    } : undefined;

    return (
      <div
        ref={this.handleviewportRefReceived}
        className={classNames('zf-carousel-viewport', viewportClassName)}
        {...viewportProps}
      >
        <div
          style={slidesStyle}
          className={classNames('zf-carousel-slides', slidesClassName)}
          {...slidesProps}
        >
          {
            Children.map(children, (child, idx)=> (
              child &&
                cloneElement(child, {
                  // we want this to be something an engineer can overwrite
                  // thus spreading the child props below it.
                  'aria-label': `Slide ${idx+1}`,
                  ...child.props,
                  'role': 'group',
                  'style': viewportWidth ? { width: viewportWidth } : undefined,
                  'aria-hidden': currentSlideIdx !== idx ? 'true': null,
                  'className': classNames('zf-carousel-slide', slideClassName, {
                    'is-hidden': currentSlideIdx !== idx
                  })
                })
            ))
          }
        </div>
      </div>
    );
  }

}

Carousel.propTypes = {
  currentSlideIdx: PropTypes.number.isRequired,
  viewportClassName: PropTypes.string,
  slidesClassName: PropTypes.string,
  slideClassName: PropTypes.string
};

export default Carousel;
